import { EnhancedDeal } from '../types';
import {
  SalesActivityTimeRange,
  filterDealsByRange,
  getDealCloseInstant,
  buildSourceMetrics,
} from './leadAnalytics';
import { formatSourceName } from './sourceUtils';

export interface CrmWinLossMetrics {
  closeRate: number;
  wonCount: number;
  lostCount: number;
  activeCount: number;
  wonRevenue: number;
  avgCycleDays: number;
  bySource: ReturnType<typeof buildSourceMetrics>;
  lossReasons: { reason: string; count: number }[];
}

export function buildCrmWinLossMetrics(
  deals: EnhancedDeal[],
  range: SalesActivityTimeRange,
  pipelineId?: string,
): CrmWinLossMetrics {
  const filtered = filterDealsByRange(deals, range, pipelineId);
  const won = filtered.filter(d => d.outcome === 'won');
  const lost = filtered.filter(d => d.outcome === 'lost');
  const active = filtered.filter(d => d.outcome === 'active');
  const closed = won.length + lost.length;

  const cycles = won
    .map(d => {
      const close = getDealCloseInstant(d);
      const start = new Date(d.createdAt).getTime();
      if (!close || Number.isNaN(start)) return null;
      return (close - start) / (1000 * 60 * 60 * 24);
    })
    .filter((x): x is number => x !== null && x >= 0);

  const reasonMap = new Map<string, number>();
  lost.forEach(d => {
    const reason = (d.lostReason || 'Unknown').trim() || 'Unknown';
    reasonMap.set(reason, (reasonMap.get(reason) || 0) + 1);
  });

  return {
    closeRate: closed > 0 ? (won.length / closed) * 100 : 0,
    wonCount: won.length,
    lostCount: lost.length,
    activeCount: active.length,
    wonRevenue: won.reduce((s, d) => s + (d.value || 0), 0),
    avgCycleDays: cycles.length ? cycles.reduce((a, b) => a + b, 0) / cycles.length : 0,
    bySource: buildSourceMetrics([], filtered),
    lossReasons: Array.from(reasonMap.entries())
      .map(([reason, count]) => ({ reason, count }))
      .sort((a, b) => b.count - a.count),
  };
}

export interface WinLossPattern {
  id: string;
  title: string;
  description: string;
  evidence?: string[];
  confidence?: string;
  tags?: string[];
}

export interface SalesWinLossDigest {
  id: string;
  report_date: string;
  summary?: string;
  winPatterns?: WinLossPattern[];
  lossPatterns?: WinLossPattern[];
  closingFactors?: { factor: string; direction: 'win' | 'loss'; weight?: number }[];
}

export function mergeBrainWithCrmMetrics(
  digest: SalesWinLossDigest | null,
  crm: CrmWinLossMetrics,
): { pattern: WinLossPattern; type: 'win' | 'loss'; crmWinRate?: number }[] {
  if (!digest) return [];
  const items: { pattern: WinLossPattern; type: 'win' | 'loss'; crmWinRate?: number }[] = [];
  (digest.winPatterns || []).forEach(p => {
    const tagSource = p.tags?.find(t => t.startsWith('source:'));
    const source = tagSource ? formatSourceName(tagSource.replace('source:', '')) : undefined;
    const row = source ? crm.bySource.find(s => s.source === source) : undefined;
    items.push({ pattern: p, type: 'win', crmWinRate: row?.winRate });
  });
  (digest.lossPatterns || []).forEach(p => {
    const tagSource = p.tags?.find(t => t.startsWith('source:'));
    const source = tagSource ? formatSourceName(tagSource.replace('source:', '')) : undefined;
    const row = source ? crm.bySource.find(s => s.source === source) : undefined;
    items.push({ pattern: p, type: 'loss', crmWinRate: row?.winRate });
  });
  return items;
}

const LABEL_STOP = new Set([
  'the', 'and', 'with', 'after', 'from', 'once', 'for', 'that', 'this', 'into',
  'without', 'a', 'an', 'of', 'to', 'in', 'on', 'or',
]);

export function normalizeWinLossLabel(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

export function winLossLabelsOverlap(a: string, b: string): boolean {
  const na = normalizeWinLossLabel(a);
  const nb = normalizeWinLossLabel(b);
  if (!na || !nb) return false;
  if (na === nb || na.includes(nb) || nb.includes(na)) return true;
  const tokens = (s: string) => s.split(' ').filter(w => w.length > 2 && !LABEL_STOP.has(w));
  const wa = new Set(tokens(na));
  const wb = tokens(nb);
  if (wa.size === 0 || wb.length === 0) return false;
  const hits = wb.filter(w => wa.has(w)).length;
  return hits >= 2;
}

export type MergedWinLossItem = {
  pattern: WinLossPattern;
  type: 'win' | 'loss';
  crmWinRate?: number;
  weight?: number;
};

export type ClosingFactor = NonNullable<SalesWinLossDigest['closingFactors']>[number];

/** Fold chip labels onto matching pattern rows so the UI can hide the duplicate strip. */
export function attachClosingFactorWeights(
  items: { pattern: WinLossPattern; type: 'win' | 'loss'; crmWinRate?: number }[],
  factors: ClosingFactor[],
): { items: MergedWinLossItem[]; leftoverFactors: ClosingFactor[] } {
  const used = new Set<number>();
  const next = items.map(item => {
    const idx = factors.findIndex(
      (f, i) =>
        !used.has(i) &&
        f.direction === item.type &&
        winLossLabelsOverlap(f.factor, item.pattern.title),
    );
    if (idx < 0) return item;
    used.add(idx);
    return { ...item, weight: factors[idx].weight };
  });
  return {
    items: next,
    leftoverFactors: factors.filter((_, i) => !used.has(i)),
  };
}
